import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Badge, Modal, Spinner } from 'react-bootstrap';
import { FaCar, FaDollarSign, FaExclamationTriangle, FaShield, FaWallet, FaCog, FaFilter, FaChartBar, FaUsers } from 'react-icons/fa';
import { API_URL } from './config';

const VehicleRentalManagement = () => {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [activeDrivers, setActiveDrivers] = useState([]);
    const [allDrivers, setAllDrivers] = useState([]);
    const [weeklySummary, setWeeklySummary] = useState(null);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [showDriverModal, setShowDriverModal] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [showFilterModal, setShowFilterModal] = useState(false);

    useEffect(() => {
        fetchManagementData();
    }, []);

    const fetchManagementData = async () => {
        try {
            setLoading(true);
            
            // Mock data for now since API endpoints may not be ready
            const mockSummary = {
                total_active_drivers: 12,
                total_weekly_target: 480,
                total_amount_paid: 325,
                drivers_missing_target: 3
            };
            
            const mockDrivers = [
                {
                    driver_id: 'DRV001',
                    name: 'John',
                    surname: 'Smith',
                    phone: '+263 771 234 567',
                    email: 'john.smith@email.com',
                    vehicle_plate_number: 'ABC123',
                    vehicle_make: 'Toyota',
                    vehicle_model: 'Corolla',
                    vehicle_ownership_type: 'xgo_owned',
                    current_balance: 120.50,
                    weekly_rental_target: 40.00,
                    weekly_paid_amount: 35.00,
                    current_week_status: 'active',
                    is_buildup_period: false,
                    agreement_start_date: '2024-01-01',
                    account_status: 'active'
                },
                {
                    driver_id: 'DRV002',
                    name: 'Jane',
                    surname: 'Doe',
                    phone: '+263 772 345 678',
                    email: 'jane.doe@email.com',
                    vehicle_plate_number: 'XYZ789',
                    vehicle_make: 'Honda',
                    vehicle_model: 'Civic',
                    vehicle_ownership_type: 'own_car',
                    current_balance: 0.00,
                    weekly_rental_target: 40.00,
                    weekly_paid_amount: 40.00,
                    current_week_status: 'completed',
                    is_buildup_period: false,
                    agreement_start_date: '2024-01-15',
                    account_status: 'active'
                },
                {
                    driver_id: 'DRV003',
                    name: 'Michael',
                    surname: 'Johnson',
                    phone: '+263 773 456 789',
                    email: 'michael.j@email.com',
                    vehicle_plate_number: 'DEF456',
                    vehicle_make: 'Nissan',
                    vehicle_model: 'Sentra',
                    vehicle_ownership_type: 'xgo_owned',
                    current_balance: 15.00,
                    weekly_rental_target: 40.00,
                    weekly_paid_amount: 25.00,
                    current_week_status: 'active',
                    is_buildup_period: true,
                    agreement_start_date: '2024-01-20',
                    account_status: 'active'
                }
            ];
            
            setWeeklySummary(mockSummary);
            setActiveDrivers(mockDrivers);
            setAllDrivers(mockDrivers);

        } catch (error) {
            console.error('Error fetching management data:', error);
            alert('Failed to load management data');
        } finally {
            setLoading(false);
        }
    };

    const onRefresh = async () => {
        setRefreshing(true);
        await fetchManagementData();
        setRefreshing(false);
    };

    const handleDriverPress = (driver) => {
        setSelectedDriver(driver);
        setShowDriverModal(true);
    };

    const filterDrivers = () => {
        let filtered = [...allDrivers];

        // Apply search filter
        if (searchText) {
            filtered = filtered.filter(driver => 
                driver.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                driver.surname?.toLowerCase().includes(searchText.toLowerCase()) ||
                driver.vehicle_plate_number?.toLowerCase().includes(searchText.toLowerCase()) ||
                driver.driver_id?.toLowerCase().includes(searchText.toLowerCase())
            );
        }

        // Apply status filter
        if (filterStatus !== 'all') {
            filtered = filtered.filter(driver => {
                const status = driver.current_week_status || 'active';
                switch (filterStatus) {
                    case 'completed':
                        return status === 'completed';
                    case 'active':
                        return status === 'active';
                    case 'missed_target':
                        return status === 'missed_target' || status === 'overdue';
                    case 'buildup':
                        return driver.is_buildup_period;
                    default:
                        return true;
                }
            });
        }

        return filtered;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'active':
                return 'primary';
            case 'missed_target':
            case 'overdue':
                return 'danger';
            default:
                return 'secondary';
        }
    };

    const getCompletionPercentage = (paid, target) => {
        if (!target || target === 0) return 0;
        return Math.round((paid / target) * 100);
    };

    const renderDriverItem = (driver, index) => {
        const completionPercentage = getCompletionPercentage(driver.weekly_paid_amount || 0, driver.weekly_rental_target || 0);
        const status = driver.current_week_status || 'active';

        return (
            <tr key={driver.driver_id} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer" onClick={() => handleDriverPress(driver)}>
                <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold mr-3">
                            {driver.name?.[0] || ''}{driver.surname?.[0] || ''}
                        </div>
                        <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {driver.name} {driver.surname}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                {driver.driver_id}
                            </div>
                        </div>
                    </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {driver.vehicle_plate_number || 'No Plate'}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                            {driver.vehicle_make} {driver.vehicle_model}
                        </div>
                    </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        driver.vehicle_ownership_type === 'xgo_owned' ? 'bg-green-100 text-green-800' :
                        driver.vehicle_ownership_type === 'own_car' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                    }`}>
                        {driver.vehicle_ownership_type === 'xgo_owned' ? 'XGO Owned' :
                         driver.vehicle_ownership_type === 'own_car' ? 'Own Car' :
                         'Non-XGO Managed'}
                    </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                        <div className="text-sm font-bold text-gray-900 dark:text-white">
                            ${driver.current_balance || 0}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">Balance</div>
                    </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                        <div className="text-sm font-bold text-gray-900 dark:text-white">
                            ${driver.weekly_paid_amount || 0}/${driver.weekly_rental_target || 0}
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-1">
                            <div 
                                className={`h-2 rounded-full ${
                                    completionPercentage >= 100 ? 'bg-green-500' : 'bg-blue-500'
                                }`}
                                style={{ width: `${Math.min(100, completionPercentage)}%` }}
                            />
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {completionPercentage}% Complete
                        </div>
                    </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        status === 'completed' ? 'bg-green-100 text-green-800' :
                        status === 'active' ? 'bg-blue-100 text-blue-800' :
                        status === 'missed_target' || status === 'overdue' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                    }`}>
                        {status}
                    </span>
                    {driver.is_buildup_period && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 ml-2">
                            Build-up
                        </span>
                    )}
                </td>
            </tr>
        );
    };

    const renderSummaryCards = () => {
        return (
            <div className="row mb-4">
                <div className="col-md-3 mb-3">
                    <Card className="text-center bg-primary text-white">
                        <Card.Body>
                            <FaUsers size={30} className="mb-2" />
                            <h3>{weeklySummary?.total_active_drivers || 0}</h3>
                            <p className="mb-0">Active Drivers</p>
                        </Card.Body>
                    </Card>
                </div>
                <div className="col-md-3 mb-3">
                    <Card className="text-center bg-success text-white">
                        <Card.Body>
                            <FaChartBar size={30} className="mb-2" />
                            <h3>${weeklySummary?.total_weekly_target || 0}</h3>
                            <p className="mb-0">Weekly Target</p>
                        </Card.Body>
                    </Card>
                </div>
                <div className="col-md-3 mb-3">
                    <Card className="text-center bg-warning text-white">
                        <Card.Body>
                            <FaDollarSign size={30} className="mb-2" />
                            <h3>${weeklySummary?.total_amount_paid || 0}</h3>
                            <p className="mb-0">Amount Paid</p>
                        </Card.Body>
                    </Card>
                </div>
                <div className="col-md-3 mb-3">
                    <Card className="text-center bg-danger text-white">
                        <Card.Body>
                            <FaExclamationTriangle size={30} className="mb-2" />
                            <h3>{weeklySummary?.drivers_missing_target || 0}</h3>
                            <p className="mb-0">Missing Target</p>
                        </Card.Body>
                    </Card>
                </div>
            </div>
        );
    };

    const renderFilterModal = () => {
        return (
            <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)} centered>
                <Modal.Header closeButton className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <Modal.Title className="text-gray-900 dark:text-white">
                        <FaFilter className="mr-2" />
                        Filter Drivers
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-white dark:bg-gray-800">
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Status Filter
                        </label>
                        {['all', 'active', 'completed', 'missed_target', 'buildup'].map(status => (
                            <div key={status} className="flex items-center mb-2">
                                <input
                                    type="radio"
                                    name="filterStatus"
                                    value={status}
                                    checked={filterStatus === status}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                                />
                                <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                    {status === 'all' ? 'All Drivers' :
                                     status === 'active' ? 'Active' :
                                     status === 'completed' ? 'Completed Target' :
                                     status === 'missed_target' ? 'Missing Target' :
                                     'Build-up Period'}
                                </label>
                            </div>
                        ))}
                    </div>
                </Modal.Body>
                <Modal.Footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                    <Button variant="primary" onClick={() => setShowFilterModal(false)}>
                        Apply Filters
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    const renderDriverModal = () => {
        if (!selectedDriver) return null;

        const completionPercentage = getCompletionPercentage(
            selectedDriver.weekly_paid_amount || 0, 
            selectedDriver.weekly_rental_target || 0
        );

        return (
            <Modal show={showDriverModal} onHide={() => setShowDriverModal(false)} size="lg" centered>
                <Modal.Header closeButton className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <Modal.Title className="text-gray-900 dark:text-white">
                        Driver Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-white dark:bg-gray-800">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                        <div className="lg:col-span-1">
                            <div className="text-center">
                                <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-500 rounded-full text-white font-bold text-xl mb-4">
                                    {selectedDriver.name?.[0] || ''}{selectedDriver.surname?.[0] || ''}
                                </div>
                                <h5 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    {selectedDriver.name} {selectedDriver.surname}
                                </h5>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{selectedDriver.driver_id}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{selectedDriver.phone}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{selectedDriver.email}</p>
                            </div>
                        </div>
                        <div className="lg:col-span-2">
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                    <FaCar className="mr-2 text-blue-500" />
                                    Vehicle Information
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Type</p>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {selectedDriver.vehicle_ownership_type === 'xgo_owned' ? 'XGO Owned' :
                                             selectedDriver.vehicle_ownership_type === 'own_car' ? 'Own Car' :
                                             'Non-XGO Managed'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Vehicle</p>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {selectedDriver.vehicle_make} {selectedDriver.vehicle_model}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Plate</p>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {selectedDriver.vehicle_plate_number}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Agreement</p>
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {new Date(selectedDriver.agreement_start_date).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                            <FaDollarSign className="mr-2 text-green-500" />
                            Financial Overview
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Current Balance</p>
                                <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                                    ${selectedDriver.current_balance || 0}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Weekly Target</p>
                                <p className="text-xl font-bold text-gray-900 dark:text-white">
                                    ${selectedDriver.weekly_rental_target || 0}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Paid This Week</p>
                                <p className="text-xl font-bold text-green-600 dark:text-green-400">
                                    ${selectedDriver.weekly_paid_amount || 0}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Remaining</p>
                                <p className="text-xl font-bold text-red-600 dark:text-red-400">
                                    ${(selectedDriver.weekly_rental_target || 0) - (selectedDriver.weekly_paid_amount || 0)}
                                </p>
                            </div>
                        </div>
                        <div className="mt-4">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Weekly Progress</p>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                                <div 
                                    className={`h-3 rounded-full ${
                                        completionPercentage >= 100 ? 'bg-green-500' : 'bg-blue-500'
                                    }`}
                                    style={{ width: `${Math.min(100, completionPercentage)}%` }}
                                />
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {completionPercentage}% of weekly target completed
                            </p>
                        </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                            <FaCog className="mr-2 text-purple-500" />
                            Quick Actions
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                            <button className="flex items-center justify-center px-4 py-2 bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800 transition-colors">
                                <FaWallet className="mr-2" />
                                Payment History
                            </button>
                            <button className="flex items-center justify-center px-4 py-2 bg-yellow-50 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-800 transition-colors">
                                <FaCog className="mr-2" />
                                Maintenance
                            </button>
                            <button className="flex items-center justify-center px-4 py-2 bg-green-50 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-100 dark:hover:bg-green-800 transition-colors">
                                <FaChartBar className="mr-2" />
                                Reports
                            </button>
                            <button className="flex items-center justify-center px-4 py-2 bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                                <FaCog className="mr-2" />
                                Settings
                            </button>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                    <Button variant="secondary" onClick={() => setShowDriverModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Loading rental management data...</p>
                </div>
            </div>
        );
    }

    const filteredDrivers = filterDrivers();

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                        <FaCar className="mr-3 text-blue-500" />
                        Vehicle Rental Management
                    </h2>
                    <div className="flex items-center space-x-4">
                        <Form.Control
                            type="text"
                            placeholder="Search drivers..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="w-64"
                        />
                        <Button variant="outline-primary" onClick={() => setShowFilterModal(true)}>
                            <FaFilter className="mr-2" />
                            Filter
                        </Button>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Active Drivers</p>
                                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{weeklySummary?.total_active_drivers || 0}</p>
                            </div>
                            <FaUsers className="text-blue-500" size={24} />
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Weekly Target</p>
                                <p className="text-2xl font-bold text-green-600 dark:text-green-400">${weeklySummary?.total_weekly_target || 0}</p>
                            </div>
                            <FaChartBar className="text-green-500" size={24} />
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Amount Paid</p>
                                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">${weeklySummary?.total_amount_paid || 0}</p>
                            </div>
                            <FaDollarSign className="text-yellow-500" size={24} />
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Missing Target</p>
                                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{weeklySummary?.drivers_missing_target || 0}</p>
                            </div>
                            <FaExclamationTriangle className="text-red-500" size={24} />
                        </div>
                    </div>
                </div>

            {/* Filter Info */}
                {(searchText || filterStatus !== 'all') && (
                    <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
                        <div className="flex items-center text-blue-800 dark:text-blue-200">
                            <FaFilter className="mr-2" />
                            <span>
                                Showing {filteredDrivers.length} of {allDrivers.length} drivers
                                {searchText && ` matching "${searchText}"`}
                                {filterStatus !== 'all' && ` with status: ${filterStatus}`}
                            </span>
                        </div>
                    </div>
                )}

                {/* Drivers Table */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                            <FaUsers className="mr-2 text-blue-500" />
                            Active Rental Drivers
                        </h3>
                    </div>
                    <div className="overflow-x-auto">
                        {filteredDrivers.length > 0 ? (
                            <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Driver</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Vehicle</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Balance</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Weekly Progress</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {filteredDrivers.map(renderDriverItem)}
                                </tbody>
                            </table>
                        ) : (
                            <div className="text-center py-12">
                                <FaCar size={50} className="text-gray-400 mx-auto mb-4" />
                                <h5 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No drivers found</h5>
                                <p className="text-gray-500 dark:text-gray-400">Try adjusting your search or filter criteria</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modals */}
            {renderFilterModal()}
            {renderDriverModal()}
        </div>
    );
};

export default VehicleRentalManagement;
